import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { bestProducts } from '../../../mocks/products';
import PaymentSelectModal from '../../../components/feature/PaymentSelectModal';

export default function Products() {
  const { ref, isVisible } = useScrollAnimation();
  const [selectedProduct, setSelectedProduct] = useState<typeof bestProducts[0] | null>(null);
  const navigate = useNavigate();

  const formatPrice = (price: number) => {
    return price.toLocaleString('ko-KR');
  };

  const handleProductClick = (product: typeof bestProducts[0]) => {
    if (product.category === 'yaksonkong') {
      const variant = product.id === 2 ? '5' : '2';
      navigate(`/products/yaksonkong?variant=${variant}`);
    } else if (product.category === 'spirulina') {
      navigate('/products/spirulina');
    } else {
      setSelectedProduct(product);
    }
  };

  return (
    <section id="products" className="py-14 md:py-24 px-5 md:px-8 bg-white">
      <div ref={ref} className="max-w-7xl mx-auto">
        <div
          className={`flex flex-col sm:flex-row sm:items-end justify-between mb-8 md:mb-14 gap-2 ${
            isVisible ? 'animate-fade-in-up' : 'opacity-0'
          }`}
        >
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-600 text-xs font-semibold mb-3 md:mb-4">
              베스트 셀러
            </span>
            <h2
              className="text-2xl md:text-3xl font-bold text-gray-900"
              style={{ fontFamily: 'Noto Serif KR, serif' }}
            >
              <strong>인기 제품</strong>
            </h2>
          </div>
          <p className="text-xs md:text-sm text-gray-400">
            모든 제품은 당일 포장 · 신선 배송됩니다
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8" data-product-shop>
          {bestProducts.map((product, index) => (
            <div
              key={product.id}
              onClick={() => handleProductClick(product)}
              className={`group bg-white rounded-2xl border border-gray-100 overflow-hidden hover-lift cursor-pointer transition-all hover:border-emerald-200 ${
                isVisible ? 'animate-fade-in-up' : 'opacity-0'
              }`}
              style={{ animationDelay: `${index * 120}ms` }}
            >
              <div className="relative w-full h-56 md:h-80 overflow-hidden bg-stone-50">
                <img
                  src={product.image}
                  alt={`${product.name} 건강식품`}
                  title={product.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                {product.badge && (
                  <span className="absolute top-2 left-2 md:top-4 md:left-4 px-2 md:px-3 py-0.5 md:py-1 rounded-full bg-emerald-600 text-white text-[10px] md:text-xs font-semibold">
                    {product.badge}
                  </span>
                )}
                {product.discount > 0 && (
                  <span className="absolute top-2 right-2 md:top-4 md:right-4 px-2 md:px-3 py-0.5 md:py-1 rounded-full bg-rose-500 text-white text-[10px] md:text-xs font-bold">
                    -{product.discount}%
                  </span>
                )}
                {/* 호버 오버레이 */}
                <div className="absolute inset-0 bg-emerald-900/0 group-hover:bg-emerald-900/10 transition-all duration-300 flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white text-emerald-700 text-xs font-bold px-4 py-2 rounded-full flex items-center gap-1.5 whitespace-nowrap">
                    <i className={`${product.category === 'yaksonkong' || product.category === 'spirulina' ? 'ri-eye-line' : 'ri-shopping-cart-line'} w-4 h-4 flex items-center justify-center`}></i>
                    {product.category === 'yaksonkong' || product.category === 'spirulina' ? '상세 보기' : '결제 방법 선택'}
                  </span>
                </div>
              </div>
              <div className="p-4 md:p-6">
                <h3 className="text-sm md:text-base font-bold text-gray-900 mb-0.5 md:mb-1">
                  {product.name}
                </h3>
                <p className="text-xs text-gray-400 mb-2 md:mb-3">{product.weight}</p>
                <div className="flex items-center gap-0.5 mb-3 md:mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`ri-star-fill text-xs w-3 h-3 flex items-center justify-center ${
                        i < Math.floor(product.rating)
                          ? 'text-amber-400'
                          : 'text-gray-200'
                      }`}
                    ></i>
                  ))}
                  <span className="text-xs text-gray-400 ml-1">
                    ({product.reviews})
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg md:text-xl font-bold text-emerald-600">
                      {formatPrice(product.price)}원
                    </span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-300 line-through">
                        {formatPrice(product.originalPrice)}원
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    {(product as any).usdtPrice && (
                      <div className="flex items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-50 border border-teal-200 text-teal-700 text-xs font-semibold">
                          <i className="ri-bit-coin-line w-3 h-3 flex items-center justify-center"></i>
                          USDT ${(product as any).usdtPrice}
                        </span>
                        <span className="text-[10px] text-gray-400">ERC-20</span>
                      </div>
                    )}
                    <span className="ml-auto text-xs text-emerald-600 font-semibold flex items-center gap-1 whitespace-nowrap">
                      구매하기
                      <i className="ri-arrow-right-line w-3 h-3 flex items-center justify-center"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 결제 방법 선택 모달 */}
      {selectedProduct && (
        <PaymentSelectModal
          product={{
            id: String(selectedProduct.id),
            name: selectedProduct.name,
            price: selectedProduct.price,
            originalPrice: selectedProduct.originalPrice,
            image: selectedProduct.image,
            category: selectedProduct.category,
            usdtPrice: (selectedProduct as any).usdtPrice,
          }}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </section>
  );
}